import { useQuery } from 'convex/react';
import { api } from '../../../convex/functions/_generated/api';
import { adminSession } from '@/lib/api';

/**
 * /dashboard — at-a-glance app stats. Backed by a single reactive
 * Convex query (api.admin.stats.dashboardStats) so the numbers
 * update live as users sign up, profiles publish, ratings land, etc.
 *
 * Auth via the explicit-token pattern (matches admin/health.ts) so
 * the SPA's useQuery can re-subscribe across signins without
 * threading ConvexAuth identity through the admin pool.
 */
export function DashboardOverview() {
  const token = adminSession.getToken() ?? '';
  const stats = useQuery(api.admin.stats.dashboardStats, { token });

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h2 className="serif" style={{ fontSize: 30, fontWeight: 500, fontStyle: 'italic', margin: 0 }}>
          Overview
        </h2>
        <div style={{ color: 'var(--ink-3)', fontSize: 13, marginTop: 4 }}>
          Live snapshot — refreshes as data changes.
          {stats && (
            <span style={{ marginLeft: 8 }}>
              Sampled {new Date(stats.now).toLocaleTimeString()}
            </span>
          )}
        </div>
      </header>

      {!stats ? (
        <div style={{ color: 'var(--ink-3)', padding: 32, textAlign: 'center' }}>
          Loading…
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Row 1 — users + signup sparkline */}
          <Row>
            <Card title="Users" subtitle="Total accounts">
              <BigNumber>{stats.users.total.toLocaleString()}</BigNumber>
              <Sparkline values={stats.users.last7_daily_buckets} />
              <MutedRow
                left={`+${stats.users.signups_24h} in 24h`}
                right={`+${stats.users.signups_7d} in 7d`}
              />
            </Card>
            <Card title="Profiles" subtitle={`${stats.profiles.published.toLocaleString()} published`}>
              <BigNumber>{stats.profiles.total.toLocaleString()}</BigNumber>
              <MutedRow
                left={`${pct(stats.profiles.published, stats.profiles.total)}% publish rate`}
                right={`${stats.profiles.total - stats.profiles.published} draft`}
              />
            </Card>
          </Row>

          {/* Row 2 — engagement */}
          <Row>
            <Card title="Ratings" subtitle="Total submitted">
              <BigNumber>{stats.engagement.ratings_total.toLocaleString()}</BigNumber>
            </Card>
            <Card title="DM messages" subtitle="Total sent">
              <BigNumber>{stats.engagement.dm_messages_total.toLocaleString()}</BigNumber>
            </Card>
            <Card
              title="Open DM reports"
              subtitle="Awaiting moderation"
              accent={stats.engagement.dm_reports_open > 0}
            >
              <BigNumber>{stats.engagement.dm_reports_open.toLocaleString()}</BigNumber>
            </Card>
          </Row>

          {/* Row 3 — monetization */}
          <Row>
            <Card title="Premium" subtitle="Active entitlements">
              <BigNumber>{stats.monetization.premium_entitlements.toLocaleString()}</BigNumber>
              <MutedRow
                left={`${pct(stats.monetization.premium_entitlements, stats.users.total)}% of users`}
                right=""
              />
            </Card>
          </Row>
        </div>
      )}
    </div>
  );
}

function pct(numerator: number, denominator: number): string {
  if (denominator <= 0) return '0';
  return ((numerator / denominator) * 100).toFixed(1);
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
      }}
    >
      {children}
    </div>
  );
}

function Card({
  title,
  subtitle,
  accent,
  children,
}: {
  title: string;
  subtitle?: string;
  accent?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: 'var(--surface)',
        border: `1px solid ${accent ? 'var(--coral)' : 'var(--line)'}`,
        borderRadius: 14,
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div>
        <div
          className="mono"
          style={{
            fontSize: 10,
            color: accent ? 'var(--coral)' : 'var(--ink-3)',
            letterSpacing: 1,
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            {subtitle}
          </div>
        )}
      </div>
      {children}
    </section>
  );
}

function BigNumber({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="serif"
      style={{
        fontSize: 38,
        fontWeight: 500,
        color: 'var(--ink)',
        lineHeight: 1.1,
      }}
    >
      {children}
    </div>
  );
}

function MutedRow({ left, right }: { left: string; right: string }) {
  return (
    <div
      className="mono"
      style={{
        fontSize: 11,
        color: 'var(--ink-3)',
        display: 'flex',
        justifyContent: 'space-between',
        gap: 8,
      }}
    >
      <span>{left}</span>
      <span>{right}</span>
    </div>
  );
}

/**
 * Tiny inline SVG sparkline. 7 data points (left → right = 6 days
 * ago → today). No chart library — ~30 LOC of path math, matches
 * the brand palette via currentColor + var(--coral).
 */
function Sparkline({ values }: { values: number[] }) {
  if (values.length === 0) return null;
  const W = 200;
  const H = 36;
  const pad = 2;
  const max = Math.max(1, ...values);
  const step = (W - pad * 2) / Math.max(1, values.length - 1);

  const points = values.map((v, i) => {
    const x = pad + i * step;
    // 0 at the bottom, max at the top — invert y.
    const y = pad + (H - pad * 2) * (1 - v / max);
    return [x, y] as const;
  });

  const d = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  // Area fill underneath the line for visual weight.
  const areaD =
    d +
    ` L${(pad + (values.length - 1) * step).toFixed(1)},${H - pad}` +
    ` L${pad.toFixed(1)},${H - pad} Z`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
    >
      <path d={areaD} fill="var(--coral)" opacity={0.12} />
      <path
        d={d}
        fill="none"
        stroke="var(--coral)"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={1.6} fill="var(--coral)" />
      ))}
    </svg>
  );
}
